import { Component, ChangeDetectionStrategy, ViewEncapsulation, OnInit, PLATFORM_ID, Inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { IAppState } from '@app/store';
import { LocaleService } from '@app/core/i18n';
import { PlatformService } from '@app/shared/services/platform/platform.service';
import { UserUtilsService } from '@app/library/services/user-utils.service';
import { IPageParam, IPageEdito } from '@vestiaire/vc-angular-lib';
import { Router, ActivatedRoute } from '@angular/router';
import { BasePageComponentDirective } from '@app/library/components/base-page.component';
import { HelpersService } from '@app/shared/services/helpers/helpers.service';
import { SetPageParams } from '@app/core/header/store/actions';
import { Persist } from '@app/analytics/actions/data-layer';
import { SearchStateMappingService } from '@app/core/search/services';
import { TransferState, Meta } from '@angular/platform-browser';
import { PageService } from '@app/core/content/services';
import { Breadcrumb } from '@vestiaire/api-sdk-angular';
import { RoutingService } from '@app/core/services/routing.service';
import { UserService } from '@app/core/user';

@Component({
  selector: 'vc-cms-edito',
  templateUrl: './cms-edito.component.html',
  styleUrls: ['./cms-edito.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class CmsEditoComponent extends BasePageComponentDirective implements OnInit {
  public pageParam: IPageParam = { path: '' };
  // Came from the resolver
  public editoPageFromResolver: IPageEdito;
  public date: string;
  public pageBreadcrumb: Array<Breadcrumb> = [];

  constructor(
    private meta: Meta,
    private pageService: PageService,
    private router: Router,
    private routingService: RoutingService,
    protected readonly store$: Store<IAppState>,
    protected readonly localeService: LocaleService,
    protected readonly transferState: TransferState,
    protected readonly helpersService: HelpersService,
    protected readonly userUtilsService: UserUtilsService,
    protected readonly searchStateMappingService: SearchStateMappingService,
    @Inject(PLATFORM_ID) protected platformId: Object,
    public readonly platformService: PlatformService,
    public route: ActivatedRoute,
    protected readonly userService: UserService
  ) {
    super(
      store$,
      localeService,
      platformService,
      userUtilsService,
      helpersService,
      searchStateMappingService,
      transferState,
      platformId,
      'edito',
      userService
    );
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;

    // Dispach the [Navigation] set page parameters
    this.store$.dispatch(new SetPageParams({ pageType: 'edito' }));
  }

  public ngOnInit() {
    const editoPathArray = this.router.url.split('/');

    if (editoPathArray.length >= 2) {
      this.pageParam['path'] = editoPathArray[2];
    }
    this.updateMetas();
  }

  updateMetas(): void {
    this.route.data.subscribe(
      data => {
        if (data.CMS) {
          const { body, category } = data.CMS;
          this.initBreadcrumb(body.title, body.url, body.category, category);
          const dateRegExp = /([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/;
          const match = data.CMS.start.match(dateRegExp);
          this.date = match && match[1] ? match[1] : null;
          this.editoPageFromResolver = data.CMS;
          this.pageService.setTitle(body.title + ' - Journal | Vestiaire Collective');
          this.meta.updateTag({
            name: 'description',
            content: body.excerpt.length > 200 ? body.excerpt.substr(0, 197) + '...' : body.excerpt
          });
          switch (this.localeService.getTranslationKey()) {
            case 'us':
            case 'en':
              this.pageService.setLanguageVersion('/journal/' + data.CMS.body.uri + '/', 'en-US');
              this.pageService.setLanguageVersion('/journal/' + data.CMS.body.uri + '/', 'en-GB');
              break;
          }
          if (data.CMS.noIndex || (body && body.noIndex)) {
            this.meta.updateTag({ name: 'robots', content: 'noindex' });
          } else {
            this.meta.updateTag({ name: 'robots', content: 'index, follow' });
          }

          this.store$.dispatch(
            new Persist({
              ...data.pageAnalytics,
              pageUrl: data.CMS.tracking
            })
          );
        }
      },
      error => console.log('CMS Edito page error Router subscribe :', error)
    );
  }

  initBreadcrumb(title: string, path: string, catName: string, category: string) {
    const hostname = this.routingService.getHostname();
    const domain = 'https://' + hostname;
    const journalUrl = domain + '/journal/';
    this.pageBreadcrumb = [
      { localizedName: 'Journal', url: { url: journalUrl, path: 'journal/' } },
      { localizedName: catName, url: { url: `${journalUrl}cat/${category}/`, path: `/journal/cat/${category}/` } },
      { localizedName: title, url: { url: `${journalUrl}${path}/`, path: `${path}/` } }
    ];
  }
}
